import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { AppShell } from "@/components/AppShell";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { toDMY } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);
const rs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export default function ExpenseReport() {
  const [profile, setProfile] = useState(null);
  const [heads, setHeads] = useState([]);
  const [head, setHead] = useState("");
  const [supplier, setSupplier] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => ({ head, supplier, date_from: from, date_to: to });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expense-report", { params: params() });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    try {
      const res = await api.get("/expense-report/export", {
        params: { ...params(), format },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "pdf" ? "expense-ledger.pdf" : "expense-ledger.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export the expense ledger");
    }
  };

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/masters/expense_head").then(({ data }) => setHeads(data.map((m) => m.name))).catch(() => {});
    api.get("/masters/supplier").then(({ data }) => setSuppliers(data.map((m) => m.name))).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Display</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Expense Ledger</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Every bill expense entered on a purchase voucher — packing &amp; forwarding, freight and more.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-5 sm:items-end">
          <div>
            <L>Expense head</L>
            <select
              data-testid="expense-head-filter"
              className="mt-2 h-10 w-full rounded-sm border border-zinc-300 bg-white px-2 text-sm"
              value={head}
              onChange={(e) => setHead(e.target.value)}
            >
              <option value="">All heads</option>
              {heads.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
          <div>
            <L>Supplier</L>
            <select
              data-testid="expense-supplier-filter"
              className="mt-2 h-10 w-full rounded-sm border border-zinc-300 bg-white px-2 text-sm"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            >
              <option value="">All suppliers</option>
              {suppliers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <L>From</L>
            <DateInput data-testid="expense-from-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={from} onChange={(v) => setFrom(v)} />
          </div>
          <div>
            <L>To</L>
            <DateInput data-testid="expense-to-input" className="mt-2 rounded-sm" inputClassName="h-9 w-full rounded-sm border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-[var(--mod-primary)]" value={to} onChange={(v) => setTo(v)} />
          </div>
          <div className="flex gap-2">
            <Button data-testid="expense-run-button" className="rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" onClick={load}>
              Show
            </Button>
            <Button data-testid="expense-export-pdf" variant="outline" className="rounded-sm" onClick={() => download("pdf")}>
              <Download size={14} className="mr-1.5" /> PDF
            </Button>
            <Button data-testid="expense-export-excel" variant="outline" className="rounded-sm" onClick={() => download("excel")}>
              Excel
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : !data || data.rows.length === 0 ? (
          <div data-testid="expense-empty-state" className="border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">No expenses yet</h3>
            <p className="mt-1 text-sm text-zinc-500">Add bill expenses in the Bill Expenses panel of a purchase voucher.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4" data-testid="expense-totals">
              {[
                ["Entries", data.totals.entries],
                ["Expense amount", rs(data.totals.amount)],
                ["GST on expenses", rs(data.totals.tax)],
              ].map(([label, value]) => (
                <div key={label} className="border border-zinc-200 bg-white p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                  <p className="font-mono-num mt-1.5 text-lg font-semibold">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 border border-zinc-200 bg-white" data-testid="expense-by-head">
              <div className="bg-zinc-50 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                Head wise total
              </div>
              <div className="divide-y divide-zinc-100">
                {data.by_head.map((h) => (
                  <div key={h.head} data-testid={`expense-head-total-${h.head}`} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="font-semibold uppercase">
                      {h.head} <span className="ml-1 text-[11px] font-normal text-zinc-400">{h.count} entry(s)</span>
                    </span>
                    <span className="font-mono-num font-semibold">{rs(h.amount)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 overflow-x-auto border border-zinc-200 bg-white" data-testid="expense-table">
              <table className="w-full text-sm">
                <thead className="border-b border-zinc-200">
                  <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                    {["Date", "Vch no.", "Bill no.", "Supplier", "Head", "%", "Taxable", "Amount", "GST"].map((h) => (
                      <th key={h} className={`px-3 py-2 ${["Amount", "GST"].includes(h) ? "text-right" : "text-left"}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {data.rows.map((r, i) => (
                    <tr key={`${r.ref_no}-${r.head}-${i}`} data-testid={`expense-row-${i}`} className="hover:bg-zinc-50">
                      <td className="font-mono-num px-3 py-2 whitespace-nowrap">{toDMY(r.date)}</td>
                      <td className="px-3 py-2 font-semibold">{r.ref_no}</td>
                      <td className="font-mono-num px-3 py-2">{r.bill_no || "-"}</td>
                      <td className="px-3 py-2">{r.supplier || "-"}</td>
                      <td className="px-3 py-2 uppercase">{r.head}</td>
                      <td className="font-mono-num px-3 py-2">{r.pct ? `${r.pct}%` : "-"}</td>
                      <td className="px-3 py-2">{r.taxable ? "Yes" : "No"}</td>
                      <td className="font-mono-num px-3 py-2 text-right">{rs(r.amount)}</td>
                      <td className="font-mono-num px-3 py-2 text-right">{rs(r.tax)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </AppShell>
  );
}
